import "./application";
import '../apps/login/main';
import '../apps/shareable_copy_modal/main';
import '../apps/on_boarding/main.ts';
import '../utils/helpers/datetimeLocalization';
import '../apps/planned_trails/main.ts';
import '../apps/import_recording/main.ts';
import '../apps/recorded_trails/main.ts';
import '../apps/recording_crop_app/main.ts';
import '../apps/flyover_preview/main.ts';
import '../apps/flyover_share/main.ts';
import '../apps/recording_map/main.ts';
import '../apps/plan_map/main.ts';
import '../apps/purchases/main';
import '../apps/hikes/main';
import '../apps/hike_map/main.ts';
import '../apps/download_modal/main.ts';
import '../apps/comments/main';
import '../apps/edit_recording_details/main.ts';
import '../apps/embed_builder/main';
import { auth, signOut, sendPasswordResetEmail } from "../utils/firebase/auth";
import { initAuthSync } from "../utils/firebase/authSync";
import { OnBoardingGoal } from '../utils/models/OnBoardingGoal';
import { openFlyoverVideoFullscreen } from '../utils/openFlyoverVideoFullscreen';

// Firebase is already initialized via the static import above.
// Call initAuthSync synchronously so onAuthStateChanged starts listening
// immediately — no dynamic-import delay.
if ((window as any).firebaseConfigs) {
  initAuthSync();
}
import { RevenueCatWrapper } from '../utils/RevenueCatWrapper';
import { flyoverShareStyleUrl } from '../apps/flyover_share/flyoverShareStyle';

// Recording show page button functionality
const gpxDownloadButtons = document.querySelectorAll('.gpx-download');
const showMapboxModalButtons = document.querySelectorAll('.show-mapbox-modal');
const addToTrailListButton = document.querySelector('.add-to-trail-list');
const copyRecordingButtons = document.querySelectorAll('.copy-recording');
const printMapDownloadButtons = document.querySelectorAll('.print-map-download');
// Send to Garmin buttons - we'll handle these by checking text content

if (gpxDownloadButtons) {
  gpxDownloadButtons.forEach((gpxDownloadButton) => {
    gpxDownloadButton.addEventListener('click', () => {
      window.openDownloadModal();
    });
  });
}

// The recording's share config lives on the "Generate 3D flyover" dropdown item's data-* attributes
// (populated server-side). Both the 3D flyover preview (so its in-preview "Share video" button works)
// and the "Generate 3D flyover" action read from here.
function recordingShareConfig() {
  const el = document.querySelector('.generate-3d-flyover') as HTMLElement | null;
  const ds = el?.dataset || {};
  return {
    recordingId: ds.recordingId || String(window.recordingId || ''),
    shareUrl: ds.shareUrl || window.recordingShareUrl || '',
    title: ds.title || window.recordingName || '',
    distance: ds.distance || '',
    elevation: ds.elevation || '',
    duration: ds.duration || '',
    thumbnailUrl: ds.thumbnailUrl || '',
  };
}

if (showMapboxModalButtons) {
  showMapboxModalButtons.forEach((showMapboxModalButton) => {
    showMapboxModalButton.addEventListener('click', () => {
      if (typeof window.showMapModalForRecordedTrail === 'function') {
        window.showMapModalForRecordedTrail(window.recordingId, recordingShareConfig());
      }
    });
  });
}

// "Generate 3D flyover" — opens the share wizard with the recording's data.
document.querySelectorAll('.generate-3d-flyover').forEach((button) => {
  button.addEventListener('click', async () => {
    const show = (window as unknown as { showFlyoverShare?: (cfg: unknown) => void }).showFlyoverShare;
    if (typeof show !== 'function') return;
    // Render in the user's currently-selected map style (like the recording map / flyover preview),
    // so the post-save "Generate 3D flyover" path sends a concrete style, not the backend default.
    const isPro = await RevenueCatWrapper.isProOrProPlus();
    show({ ...recordingShareConfig(), mapStyleUrl: flyoverShareStyleUrl(isPro), source: 'recording_overview' });
  });
});

// Play a ready 3D flyover video full screen when its gallery thumbnail is tapped. The overlay
// builder moved to utils/openFlyoverVideoFullscreen so the comment media strip can open the same
// player from a Vue component.
document.querySelectorAll('.play-flyover-video').forEach((el) => {
  el.addEventListener('click', (e) => {
    e.preventDefault();
    const url = (el as HTMLElement).dataset.videoUrl;
    if (url) openFlyoverVideoFullscreen(url);
  });
});

if (addToTrailListButton) {
  addToTrailListButton.addEventListener('click', () => {
    window.onBoardingVue.launchFor(OnBoardingGoal.AddTrailToList);
  });
}

if (copyRecordingButtons) {
  copyRecordingButtons.forEach((copyRecordingButton) => {
    copyRecordingButton.addEventListener('click', () => {
      const copyUrl = copyRecordingButton.getAttribute('data-copy-url');
      if (copyUrl) {
        navigator.clipboard.writeText(copyUrl).then(() => {
          // Show toast notification
          const toastEl = document.getElementById('copy-toast');
          if (toastEl) {
            const toast = new bootstrap.Toast(toastEl);
            toast.show();
          }
        }).catch(err => {
          console.error('Failed to copy URL: ', err);
        });
      }
    });
  });
}

if (printMapDownloadButtons) {
  printMapDownloadButtons.forEach((printMapDownloadButton) => {
    printMapDownloadButton.addEventListener('click', async () => {
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
      if (isProOrProPlus) {
        window.location.href = window.printingUrl;
      } else {
        window.location.href = '/purchases';
      }
    });
  });
}

// Handle Send to Garmin dropdown items
const dropdownItems = document.querySelectorAll('.dropdown-item');
if (dropdownItems) {
  dropdownItems.forEach((dropdownItem) => {
    if (dropdownItem.textContent?.trim() === 'Send to Garmin') {
      dropdownItem.addEventListener('click', () => {
        // Open download modal directly on the Garmin tab
        if (window.openDownloadModal) {
          window.openDownloadModal('gps');
        }
      });
    }
  });
}

// Handle Edit Details button
const editRecordingButtons = document.querySelectorAll('.edit-recording-details');
if (editRecordingButtons) {
  editRecordingButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const recordingId = button.getAttribute('data-recording-id');
      if (recordingId && typeof window.showEditRecordingModal === 'function') {
        window.showEditRecordingModal(recordingId);
      }
    });
  });
}

// Handle Delete Recording button
const deleteRecordingButtons = document.querySelectorAll('.delete-recording');
if (deleteRecordingButtons) {
  deleteRecordingButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const recordingId = button.getAttribute('data-recording-id');
      if (!recordingId) return;

      if (confirm('Are you sure you want to delete this recording? This action cannot be undone.')) {
        try {
          // Import the GraphQL client and mutation
          const { default: graph_client } = await import('../utils/graphql/graph_client');
          const { DestroyRecording } = await import('../utils/graphql/recorded-trail-graph');

          await graph_client.request(DestroyRecording, {
            input: { id: recordingId }
          });

          // Redirect to recordings list after deletion
          window.location.href = '/user/recordings';
        } catch (error) {
          console.error('Error deleting recording:', error);
          alert('Failed to delete recording. Please try again.');
        }
      }
    });
  });
}
